'use client'

import type {
  ErrorDetails,
  LogContext,
  LogDetails,
  Logger,
  LogLevel,
  ReservedLogContextKey,
} from '@moment/observability/logger'

import {
  type Logger as DatadogLogger,
  datadogLogs,
  HandlerType,
  type LogsEvent,
  StatusType,
} from '@datadog/browser-logs'
import { toError } from '@moment/observability/logger'
import LogRocket from 'logrocket'

import { MomentEnv } from '@/app/moment-env'
import { Environment } from '@/types/enums/environments'

// Every top-level LogsEvent field must be a banned LogContext key — message
// context merges into the event last, so an unbanned field is clobber-able.
// Stops compiling when an SDK upgrade adds an event field ('error' is exempt:
// LogDetails owns it). The key remap strips LogsEvent's string index signature.
type KnownLogsEventKey = keyof {
  [K in keyof LogsEvent as string extends K ? never : K]: never
}
type _Assert<T extends true> = T
type _ReservedKeysCoverLogsEvent = _Assert<
  [Exclude<KnownLogsEventKey, 'error' | ReservedLogContextKey>] extends [never]
    ? true
    : false
>

const DEPLOYED_ENVIRONMENTS = new Set<string>([
  Environment.STAGING,
  Environment.PROD_PAPER,
  Environment.PROD,
])

const isProductionBuild = process.env.NODE_ENV === 'production'
const isKnownDeployedEnvironment =
  MomentEnv.Environment !== undefined &&
  DEPLOYED_ENVIRONMENTS.has(MomentEnv.Environment)

// Datadog/LogRocket are browser-only SDKs — @datadog/browser-logs reads
// document.referrer on every handled log, which throws during SSR. Server-side
// calls fall back to the console so they surface in Vercel function logs.
const isBrowser = typeof window !== 'undefined'

const isRemoteLoggingEnabled =
  isBrowser && isProductionBuild && isKnownDeployedEnvironment

function getDatadogLogger(loggerName?: string): DatadogLogger | null {
  if (!isRemoteLoggingEnabled) {
    return null
  }

  if (loggerName === undefined) {
    return datadogLogs.logger
  }

  return datadogLogs.createLogger(loggerName, {
    level: StatusType.info,
    handler: HandlerType.http,
    context: {
      service: MomentEnv.DataDogService,
      env: MomentEnv.DataDogEnv,
      version: MomentEnv.GitHash,
    },
  })
}

function shouldMirrorToConsole(): boolean {
  if (!isRemoteLoggingEnabled) {
    return true
  }

  // TODO(jdechicchis): Make this configurable in DevTools
  const env = MomentEnv.Environment
  if (env === Environment.STAGING || env === Environment.PROD_PAPER) {
    return true
  }

  return false
}

function createConsoleArgs(
  loggerName: string | undefined,
  message: string,
  context?: LogContext,
  error?: Error,
): unknown[] {
  const prefix =
    loggerName !== undefined ? `[${loggerName}] ${message}` : message
  const args: unknown[] = [prefix]
  if (context !== undefined) {
    args.push(context)
  }
  if (error) {
    args.push(error)
  }
  return args
}

function logToConsole(
  level: LogLevel,
  loggerName: string | undefined,
  message: string,
  context?: LogContext,
  error?: Error,
): void {
  const args = createConsoleArgs(loggerName, message, context, error)

  switch (level) {
    case 'debug':
      console.debug(...args)
      return
    case 'info':
      console.info(...args)
      return
    case 'warn':
      console.warn(...args)
      return
    case 'error':
      console.error(...args)
      return
  }
}

// Widened return type: `logger` is a banned LogContext key, but this payload
// goes only to LogRocket, never through the Datadog message-context merge.
function createLogRocketContext(
  loggerName: string | undefined,
  context?: LogContext,
): Record<string, unknown> {
  if (loggerName === undefined) {
    return { ...context }
  }
  return {
    logger: loggerName,
    ...context,
  }
}

function logToLogRocket(
  level: LogLevel,
  loggerName: string | undefined,
  message: string,
  context?: LogContext,
  error?: Error,
): void {
  const logRocketContext = createLogRocketContext(loggerName, context)

  switch (level) {
    case 'debug':
      LogRocket.debug(message, logRocketContext, error)
      return
    case 'info':
      LogRocket.info(message, logRocketContext, error)
      return
    case 'warn':
      LogRocket.warn(message, logRocketContext, error)
      return
    case 'error':
      LogRocket.error(message, logRocketContext, error)
      return
  }
}

interface ExtractedLogDetails {
  readonly context?: LogContext
  readonly error?: Error
}

function extractLogDetails(details?: LogDetails): ExtractedLogDetails {
  if (details === undefined) {
    return {}
  }

  const { error, ...context } = details
  const normalizedError = error === undefined ? undefined : toError(error)
  return { context, error: normalizedError }
}

export function createLogger(loggerName?: string): Logger {
  const datadogLogger = getDatadogLogger(loggerName)
  const mirrorToConsole = shouldMirrorToConsole()

  const log = (
    level: LogLevel,
    message: string,
    context?: LogContext,
    error?: Error,
  ): void => {
    if (mirrorToConsole) {
      logToConsole(level, loggerName, message, context, error)
    }

    if (isRemoteLoggingEnabled) {
      datadogLogger?.[level](message, context, error)
      logToLogRocket(level, loggerName, message, context, error)
    }
  }

  return {
    debug: (message, details) => {
      const { context, error } = extractLogDetails(details)
      log('debug', message, context, error)
    },
    info: (message, details) => {
      const { context, error } = extractLogDetails(details)
      log('info', message, context, error)
    },
    warn: (message, details) => {
      const { context, error } = extractLogDetails(details)
      log('warn', message, context, error)
    },
    error: (message, details: ErrorDetails) => {
      const { context, error } = extractLogDetails(details)
      log('error', message, context, error)
    },
  }
}

export const globalLogger = createLogger()
